import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faMagicWandSparkles,
  faPaperPlane,
  faSpinner,
  faWandMagicSparkles,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { emailAIService } from "../../Services/emailAIService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Style/QuickEmailModal.css";

const toneOptions = ["Professional", "Friendly", "Formal", "Marketing", "Sales"];
const purposeOptions = [
  "Product Promotion",
  "Customer Follow-up",
  "Lead Nurturing",
  "Meeting Invitation",
  "Offer & Discount",
  "Payment Reminder",
  "General Inquiry",
];

export default function QuickEmailModal({ isOpen, onClose, contact, contactType }) {
  const [step, setStep] = useState(1); // 1 = configure, 2 = review & send
  const [isLoading, setIsLoading] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    purpose: "Customer Follow-up",
    tone: "Professional",
    productName: "",
    keywords: "",
    instructions: "",
  });
  const [draft, setDraft] = useState({
    subject: "",
    greeting: "",
    body: "",
    cta: "",
  });

  const contactName =
    contact?.customerName ||
    contact?.leadName ||
    contact?.name ||
    contact?.vendorName ||
    "Contact";

  const contactEmail =
    contact?.email ||
    contact?.customerEmail ||
    contact?.leadEmail ||
    contact?.vendorEmail ||
    "";

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setAiConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleDraftChange = (e) => {
    const { name, value } = e.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    if (!aiConfig.productName) {
      toast.error("Please enter a product/service name.");
      return;
    }
    setIsLoading(true);
    try {
      const audience = `${contactType} - ${contactName}`;
      const generated = await emailAIService.generateEmailDraft({
        ...aiConfig,
        audience,
      });
      setDraft({
        subject: generated.subject ? generated.subject.replace(/\{\{CustomerName\}\}/g, contactName) : "",
        greeting: generated.greeting
          ? generated.greeting.replace(/\{\{CustomerName\}\}/g, contactName)
          : `Hello ${contactName},`,
        body: generated.body ? generated.body.replace(/\{\{CustomerName\}\}/g, contactName) : "",
        cta: generated.cta ? generated.cta.replace(/\{\{CustomerName\}\}/g, contactName) : "",
      });
      setStep(2);
      toast.success("Email draft generated!");
    } catch (err) {
      toast.error("Failed to generate draft.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!contactEmail) {
      toast.error("No email address found for this contact.");
      return;
    }
    setIsLoading(true);
    try {
      await emailAIService.sendBulkEmail({
        recipients: [{ name: contactName, email: contactEmail }],
        emailContent: draft,
      });
      toast.success(`Email sent to ${contactName} successfully!`);
      handleClose();
    } catch (err) {
      toast.error("Failed to send email.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setDraft({ subject: "", greeting: "", body: "", cta: "" });
    setAiConfig({
      purpose: "Customer Follow-up",
      tone: "Professional",
      productName: "",
      keywords: "",
      instructions: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="qem-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="qem-modal">
        {/* Header */}
        <div className="qem-header">
          <div className="qem-header-left">
            <div className="qem-icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <div>
              <h5 className="qem-title">Send Email</h5>
              <p className="qem-subtitle">
                To: <strong>{contactName}</strong>
                {contactEmail && (
                  <span className="qem-email-badge">{contactEmail}</span>
                )}
              </p>
            </div>
          </div>
          <button className="qem-close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="qem-steps">
          <div className={`qem-step ${step >= 1 ? "active" : ""} ${step > 1 ? "done" : ""}`}>
            <div className="qem-step-dot">{step > 1 ? "✓" : "1"}</div>
            <span>Configure</span>
          </div>
          <div className="qem-step-line"></div>
          <div className={`qem-step ${step >= 2 ? "active" : ""}`}>
            <div className="qem-step-dot">2</div>
            <span>Review & Send</span>
          </div>
        </div>

        {/* Body */}
        <div className="qem-body">
          {/* Step 1: Configure */}
          {step === 1 && (
            <div className="qem-step-content">
              <div className="qem-row">
                <div className="qem-field">
                  <label>Purpose</label>
                  <select name="purpose" value={aiConfig.purpose} onChange={handleConfigChange}>
                    {purposeOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="qem-field">
                  <label>Tone</label>
                  <select name="tone" value={aiConfig.tone} onChange={handleConfigChange}>
                    {toneOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="qem-field">
                <label>
                  Product / Service Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="productName"
                  value={aiConfig.productName}
                  onChange={handleConfigChange}
                  placeholder="e.g. ENTELI-PRO CRM"
                />
              </div>
              <div className="qem-field">
                <label>Keywords (optional)</label>
                <input
                  type="text"
                  name="keywords"
                  value={aiConfig.keywords}
                  onChange={handleConfigChange}
                  placeholder="e.g. discount, upgrade, demo"
                />
              </div>
              <div className="qem-field">
                <label>Additional Instructions (optional)</label>
                <textarea
                  name="instructions"
                  value={aiConfig.instructions}
                  onChange={handleConfigChange}
                  rows={2}
                  placeholder="Any specific points to include..."
                />
              </div>
              <button className="qem-generate-btn" onClick={handleGenerate} disabled={isLoading}>
                {isLoading ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> &nbsp;Generating...</>
                ) : (
                  <><FontAwesomeIcon icon={faMagicWandSparkles} /> &nbsp;Generate with AI</>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Review & Send */}
          {step === 2 && (
            <div className="qem-step-content">
              <div className="qem-field">
                <label>Subject Line</label>
                <input
                  type="text"
                  name="subject"
                  value={draft.subject}
                  onChange={handleDraftChange}
                />
              </div>
              <div className="qem-field">
                <label>Greeting</label>
                <input
                  type="text"
                  name="greeting"
                  value={draft.greeting}
                  onChange={handleDraftChange}
                />
              </div>
              <div className="qem-field">
                <label>Email Body</label>
                <textarea
                  name="body"
                  value={draft.body}
                  onChange={handleDraftChange}
                  rows={7}
                />
              </div>
              <div className="qem-field">
                <label>Call to Action (optional)</label>
                <input
                  type="text"
                  name="cta"
                  value={draft.cta}
                  onChange={handleDraftChange}
                  placeholder="e.g. Click here to learn more"
                />
              </div>

              {!contactEmail && (
                <div className="qem-warning">
                  ⚠️ No email address found for this contact. Please add an email first.
                </div>
              )}

              <div className="qem-actions">
                <button className="qem-back-btn" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button
                  className="qem-send-btn"
                  onClick={handleSend}
                  disabled={isLoading || !contactEmail}
                >
                  {isLoading ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> &nbsp;Sending...</>
                  ) : (
                    <><FontAwesomeIcon icon={faPaperPlane} /> &nbsp;Send Email</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
